import React, { createContext, useState, useEffect, useContext } from 'react';
import * as AuthService from '../api/authService';

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // Check if user is already logged in
    const checkUserAuth = async () => {
      try {
        const userData = await AuthService.checkAuth();
        if (userData) {
          setUser(userData);
        }
      } catch (err) {
        console.error('Authentication check failed:', err);
      } finally {
        setLoading(false);
      }
    };

    checkUserAuth();
  }, []);

  const login = async (username, password) => {
    try {
      setError(null);
      
      const userData = await AuthService.login(username, password);
      // Update user state
      setUser({
        userId: userData.user_id,
        role: userData.role,
        name: userData.name,
        token: userData.access_token,
        email: userData.email
      });
      
      return true;
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed');
      return false;
    }
  };

  const register = async (userData) => {
    try {
      setError(null);
      await AuthService.register(userData);
      return true;
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed');
      return false;
    }
  };

  const logout = () => {
    AuthService.logout();
    setUser(null);
  };

  const isAdmin = () => {
    return user?.role === 'admin';
  };

  const isExpert = () => {
    return user?.role === 'expert';
  };

  const hasRole = (requiredRole) => {
    if (!user) return false;
    
    // Admin has access to everything
    if (user.role === 'admin') return true;
    
    // Expert has access to expert and user routes
    if (user.role === 'expert' && (requiredRole === 'expert' || requiredRole === 'user')) return true;

    if (user.role === 'super_expert' && (requiredRole === 'super_expert' || requiredRole === 'user')) return true;
    
    // Normal user has access only to user routes
    if (user.role === 'user' && requiredRole === 'user') return true;
    
    return false;
  };

  return (
    <AuthContext.Provider value={{ 
      user, 
      loading,
      error,
      login, 
      register, 
      logout, 
      isAdmin, 
      isExpert,
      hasRole 
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;